import React, { useEffect } from 'react';

import cx from '../../utils/classnames';
import { cancelIdleCallbackPolyfill, requestIdleCallbackPolyfill } from '../../utils/polyfill';

import styles from './videoPlayer.module.scss';

type VideoPlayerProps = {
  autoplay?: boolean;
  className?: string;
  hasControls?: boolean;
  height?: number;
  orderNumber?: number /* To differentiate the video player instance */;
  refId: string;
  serviceId: string;
  useResizeObserver?: boolean /* Enable ResizeObserver for better dimension detection (default: false) */;
  videoId?: string;
  width?: number;
  onLoad?: () => void;
  onError?: () => void;
};

const noop = () => {};

const BASE_URL = 'https://stream.cms.rakuten.co.jp/gate/play_pf/';
const MIN_EMBED_DIMENSION = 100;

function VideoPlayer({
  autoplay = false,
  className,
  hasControls = false,
  height,
  orderNumber,
  refId,
  serviceId,
  useResizeObserver = false,
  videoId,
  width,
  onLoad = noop,
  onError = noop,
}: VideoPlayerProps) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const targetElementId = React.useRef(
    orderNumber
      ? `vpf-${videoId}-${orderNumber}`
      : `vpf-${videoId}-${Math.random().toString(36).slice(-5)}`,
  ).current;

  useEffect(() => {
    const embedVideoPlayer = (observedWidth?: number, observedHeight?: number) => {
      if (wrapperRef.current) {
        const wrapperHeight = observedHeight ?? wrapperRef.current.clientHeight;
        const wrapperWidth = observedWidth ?? wrapperRef.current.clientWidth;

        const params: Record<string, string | undefined> = {
          ref_id: refId,
          sid: serviceId,
          mid: videoId,
          sel: `#${targetElementId}`,
          w: width?.toString() || wrapperWidth.toString(),
          h: height?.toString() || wrapperHeight.toString(),
          ctr: hasControls ? '1' : undefined,
          a: autoplay ? '1' : undefined,
        };

        const scriptUrl = new URL(BASE_URL);
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            scriptUrl.searchParams.set(key, value);
          }
        });

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = scriptUrl.toString();
        script.onload = onLoad;
        script.onerror = onError;

        wrapperRef.current.appendChild(script);
      }
    };

    /**
     * Initialize video player after the parent is fully laid out
     * to match parent element's width and height
     */
    let observer: ResizeObserver | null = null;
    let timeoutId: number | null = null;

    if (useResizeObserver && typeof window !== 'undefined' && wrapperRef.current) {
      // New behavior: Use ResizeObserver to wait for wrapper to reach its final stable size
      observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (
          entry?.contentRect.width >= MIN_EMBED_DIMENSION &&
          entry?.contentRect.height >= MIN_EMBED_DIMENSION
        ) {
          // Clear previous timeout
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          // Wait for size to stabilize before embedding
          timeoutId = window.setTimeout(() => {
            embedVideoPlayer(entry.contentRect.width, entry.contentRect.height);
            observer?.disconnect();
          }, 100);
        }
      });
      observer.observe(wrapperRef.current);
    } else if (typeof window !== 'undefined') {
      // Old behavior: use requestIdleCallback polyfill
      if (!window.requestIdleCallback) {
        window.requestIdleCallback = requestIdleCallbackPolyfill;
        window.cancelIdleCallback = cancelIdleCallbackPolyfill;
      }
      window.requestIdleCallback(() => embedVideoPlayer());
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      observer?.disconnect();
    };
  }, [videoId, refId, serviceId, targetElementId, useResizeObserver]);

  return (
    <div
      ref={wrapperRef}
      data-testid="vpf-player"
      id={targetElementId}
      className={cx(styles.wrapper, className)}
    ></div>
  );
}

export default VideoPlayer;
